/**
 * Convert HTML string to plain text
 * Removes HTML tags and decodes HTML entities
 */
export function htmlToText(html: string): string {
  // Create a temporary div element to parse HTML
  const temp = document.createElement('div');
  temp.innerHTML = html;
  
  // Remove script and style elements
  const scripts = temp.querySelectorAll('script, style');
  scripts.forEach(el => el.remove());
  
  // Replace br tags with newlines
  const brs = temp.querySelectorAll('br');
  brs.forEach(br => br.replaceWith('\n'));
  
  // Replace p, div, and other block elements with newlines
  const blocks = temp.querySelectorAll('p, div, h1, h2, h3, h4, h5, h6, li, tr');
  blocks.forEach(block => {
    if (block.textContent?.trim()) {
      block.prepend('\n');
      block.append('\n');
    }
  });
  
  // Get text content
  let text = temp.textContent || temp.innerText || '';
  
  // Clean up excessive whitespace
  text = text
    .replace(/\n\s*\n\s*\n/g, '\n\n') // Replace multiple newlines with max 2
    .replace(/[ \t]+/g, ' ') // Replace multiple spaces/tabs with single space
    .replace(/^\s+|\s+$/g, '') // Trim start and end
    .replace(/\n[ \t]+/g, '\n') // Remove spaces at start of lines
    .replace(/[ \t]+\n/g, '\n'); // Remove spaces at end of lines
  
  return text;
}

interface BinaryContent {
  data?: string;
  contentType?: string;
  type?: string;
}

/**
 * Extract text content from note content that might be string, HTML, or binary object
 */
export function extractNoteText(content: string | BinaryContent | null | undefined): string {
  if (!content) {
    return 'No content available';
  }
  
  // If it's a binary object with HTML data
  if (typeof content === 'object' && content.data) {
    let textContent = content.data;

    // Check if it's HTML content
    if (content.contentType?.includes('html') || content.type === 'html') {
      try {
        // Try to decode from base64
        const decoded = atob(textContent);
        textContent = decoded;
      } catch {
        // If decoding fails, assume it's already plain HTML
      }

      // Convert HTML to text
      return htmlToText(textContent);
    }

    // Handle plain text content (e.g., from DocumentExtractor on server)
    if (content.type === 'text' || content.contentType?.includes('text/plain')) {
      try {
        // Try to decode from base64
        const decoded = atob(textContent);
        textContent = decoded;
      } catch {
        // If decoding fails, assume it's already plain text
      }
      return textContent;
    }

    // For non-text binaries, just return a description
    if (content.type === 'pdf') {
      return '[PDF Document - content cannot be extracted as text]';
    } else if (content.type === 'image') {
      return '[Image - content cannot be extracted as text]';
    } else {
      return '[Binary Document - content cannot be extracted as text]';
    }
  }
  
  // If it's a string that might be HTML
  if (typeof content === 'string') {
    // Check if it looks like HTML
    if (content.includes('<') && content.includes('>')) {
      return htmlToText(content);
    }
    // Otherwise return as-is
    return content;
  }
  
  return 'Content format not recognized';
}